import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import { getColumnId } from '@/types/table.types';
import { TradeItem } from '@/types';
import {
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
  InformationCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { GripDotsIcon } from '@/components/shared/table-utils';
import { VirtualizedGrid } from '@/components/shared/VirtualizedGrid';
import {
  GRID_HEADER_HEIGHT_PX,
  GRID_HEADER_HEIGHT_STYLE,
  GRID_ROW_HEIGHT_PX,
  GRID_ROW_HEIGHT_STYLE,
} from '@/components/shared/grid-style';
import { PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import type { ColumnPinSide } from '@/stores/trades/mtm-column-store';
import { useMtmCrossDnd } from './MtmCrossDndContext';

// No-op strategy: header items do not displace during drag (drop position is shown
// via the blue indicator instead). Mirrors TradesTable.
const noDisplacementStrategy: SortingStrategy = () => null;
import {
  resolveDefaultPrice,
  classifyPriceInput,
  computeDelta,
  isEligibleStatus,
  isMtmDefaultUnchecked,
  isMtmEligibleRow,
  isRepoCallableInScope,
  type RepoCallScope,
} from '@/utils/trades/mtm-cell-helpers';
import {
  computeMtmRow,
  baselineMarginedValue,
  mtmBaselinePriceRaw,
  computeValueDelta,
  computeCollateralDelta,
  computeRepoValueDelta,
  computeRepoMarginValueDelta,
  effectiveQuantity,
} from '@/utils/trades/mtm-calculations';
import {
  MTM_SYNTHETIC_LAST_MARKED,
  MTM_SYNTHETIC_NEW_PRICE,
  MTM_SYNTHETIC_DELTA,
  MTM_SYNTHETIC_NEW_MARGIN,
  MTM_SYNTHETIC_VALUE_DELTA,
  MTM_SYNTHETIC_COLLATERAL_DELTA,
  MTM_SYNTHETIC_PRICE_IDS,
} from '@/stores';
import { formatMarkPrice } from '@/utils/trades/mark-price-format';
import {
  buildFooterColumnTotals,
  approxRateNote,
  describeCurrencyTotal,
} from '@/utils/trades/footer-totals';
import type { FooterColumnTotal } from '@/utils/trades/footer-totals';
import { aggregateFromBuckets } from '@/utils/trades/currency-totals';
import {
  buildMtmSyntheticCcyBuckets,
  MTM_SYNTHETIC_SUMMABLE_IDS,
} from '@/utils/trades/mtm-synthetic-totals';
import { useCurrencyDisplayStore, useFxRatesStore, useMtmDataStore } from '@/stores';

// Fixed widths for the synthetic price-block columns (Last Marked | New Price |
// Gross Price Δ | New Margin Value | Gross Value Δ | Margin Value Δ). Real columns
// fall back to col.size ?? 140.
const SYNTHETIC_WIDTHS: Record<string, number> = {
  [MTM_SYNTHETIC_LAST_MARKED]: 120,
  [MTM_SYNTHETIC_NEW_PRICE]: 140,
  [MTM_SYNTHETIC_DELTA]: 160,
  [MTM_SYNTHETIC_NEW_MARGIN]: 160,
  [MTM_SYNTHETIC_VALUE_DELTA]: 160,
  [MTM_SYNTHETIC_COLLATERAL_DELTA]: 160,
};

const SYNTHETIC_ID_SET = new Set<string>(MTM_SYNTHETIC_PRICE_IDS);

// Width of the fixed leftmost checkbox column (sticky, never pinnable/reorderable).
const CHECKBOX_WIDTH = 40;

// LR-548: repo/reverse rows join the margin-call selection (NOT the MTM run) — see isRepoCallable:
// an active|matured repo/reverse IN THE CALL CURRENCY (r2 review #1, the backend nets one
// (cp, currency) pool); matured stays selected, unlike the stock select-all which drops it.

// Stable default for the optional repo-deselection prop, so a caller that omits it (a non-repo
// test render) doesn't create a fresh Set each render and churn the memos below.
const EMPTY_REPO_EXCLUDED: Set<number> = new Set();

// Width (px) of the sticky "Total" footer label. The checkbox column is only 40px
// (too narrow for the word), so the label overlays the checkbox + the leftmost
// blank footer cells via a negative margin of the same value — it adds no layout
// width and so keeps the totals aligned under their columns (mirrors TradesTable).
const TOTAL_LABEL_WIDTH = 72;

// Extra width reserved on a column when its header gains an icon, so the one-line
// header text is never squeezed into a wrap (mirrors TradesTable's getColWidth
// pin/filter reserve). HANDLE: the drag handle shown while the Columns tab is
// active; PIN: the pin indicator shown whenever the column is pinned.
const HANDLE_RESERVE_PX = 20;
const PIN_RESERVE_PX = 20;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MtmColumnDef = ColumnDef<TradeItem, any>;

function colWidth(col: MtmColumnDef): number {
  const id = getColumnId(col);
  return SYNTHETIC_WIDTHS[id] ?? col.size ?? 140;
}

// A column's intended text alignment, read from its `meta.align` (set in
// trades-columns.tsx). Used to right-align numeric HEADERS so they sit directly
// over their right-aligned cell data instead of drifting to the left edge.
function columnAlign(col: { meta?: unknown }): 'left' | 'right' {
  return (col.meta as { align?: 'left' | 'right' } | undefined)?.align === 'right'
    ? 'right'
    : 'left';
}

// Shared sign-and-colour for a signed numeric delta. Centralises the U+2212
// minus character (easy to drift to ASCII '-') and the green/red/grey palette
// used by both the Δ and New Margin Value cells.
function signedDirection(value: number): { sign: '+' | '−'; color: string } {
  const sign = value >= 0 ? '+' : '−'; // U+2212 minus sign
  const color =
    value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-400';
  return { sign, color };
}

// ---------------------------------------------------------------------------
// Sortable header cell. Mirrors TradesTable's SortableHeaderWrapper: each header
// becomes a dnd-kit sortable (plain column id, no sidebar prefix) so columns can
// be reordered by dragging headers (table→table) and the sidebar Columns panel
// can drop onto them (sidebar→table). The drag handle (GripDotsIcon) only shows
// while the Columns tab is active, matching Trades. The fixed checkbox column is
// rendered separately and never wrapped here.
// ---------------------------------------------------------------------------
interface MtmSortableHeaderProps {
  id: string;
  width: number;
  align: 'left' | 'right';
  showHandle: boolean;
  isHighlighted: boolean;
  pinSide: ColumnPinSide;
  onUnpin?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

const MtmSortableHeader: React.FC<MtmSortableHeaderProps> = ({
  id,
  width,
  align,
  showHandle,
  isHighlighted,
  pinSide,
  onUnpin,
  onContextMenu,
  children,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });
  const { activeDragSource, tableOverColumnId } = useMtmCrossDnd();
  // Drop indicator fires for table→table AND sidebar→table drags: the provider routes a
  // sidebar drag hovering a table column into tableOverColumnId (the plain, unprefixed id)
  // — see createCrossDndProvider.handleDragOver. Keying off tableOverColumnId + any active
  // source covers both, mirroring TradesTable's SortableHeaderWrapper.
  const showDropIndicator = activeDragSource != null && tableOverColumnId === id;

  const handle = showHandle ? (
    <span
      className="flex flex-shrink-0 items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      {...attributes}
      {...listeners}
      tabIndex={0}
      role="button"
      aria-label="Drag to reorder column"
    >
      <GripDotsIcon className="h-4 w-4" />
    </span>
  ) : null;

  const pinIndicator = pinSide ? (
    <button
      type="button"
      onClick={onUnpin}
      className="flex-shrink-0 rounded p-0.5 text-blue-600 hover:text-blue-800"
      title="Unpin column"
      aria-label="Unpin column"
    >
      {pinSide === 'left' ? <PinLeftIcon className="h-3 w-3" /> : <PinRightIcon className="h-3 w-3" />}
    </button>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      data-column-id={id}
      onContextMenu={onContextMenu}
      style={{
        width,
        minWidth: width,
        visibility: isDragging && activeDragSource === 'table' ? 'hidden' : undefined,
      }}
      className={clsx(
        'relative flex items-center gap-1 px-2 cursor-pointer',
        align === 'right' ? 'justify-end text-right' : 'justify-start',
        isHighlighted && 'self-stretch -my-2 py-2 bg-blue-50 animate-column-highlight-out',
      )}
    >
      {showDropIndicator && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
      )}
      {handle}
      <span className="min-w-0 whitespace-nowrap">{children}</span>
      {pinIndicator}
    </div>
  );
};

export interface MtmTableProps {
  data: TradeItem[];
  // Ordered + visibility-filtered columns from the MTM column store. Synthetic
  // price-block ids (placeholder defs) are interleaved at their store positions;
  // MtmTable renders those cells itself from per-row helpers.
  columns: MtmColumnDef[];
  // Pinned column ids (left/right) from the MTM column store. The checkbox column
  // is always the fixed leftmost cell and is never part of these.
  pinnedLeft?: string[];
  pinnedRight?: string[];
  isLoading: boolean;
  error: Error | null;
  activeCounterpartyName: string;
  priceOverrides: Map<number, string>;
  excludedIds: Set<number>;
  eligibleIds: Set<number>; // ids whose status is active|matured (from MtmPage)
  onSetOverride: (id: number, price: string) => void;
  onClearOverride: (id: number) => void;
  onToggleExclude: (id: number) => void;
  onSetExcludedAll: (ids: number[]) => void; // replace whole excluded set (header all/none)
  // Reports whether any INCLUDED (eligible, not-excluded) row currently has an invalid price,
  // so the parent can block the Run-MTM submit instead of silently marking it at its default.
  onValidityChange?: (hasInvalidIncluded: boolean) => void;
  // LR-548: repo/reverse margin-call selection (DESELECTED ids). Separate from excludedIds —
  // repo is out of Run-MTM and its select-all keeps matured rows. `priceOverrides` is shared.
  // Optional (default: empty set / no-ops) so non-repo callers/tests need not wire them.
  repoExcludedIds?: Set<number>;
  // The ids the PAGE has selected for the margin call (callable, not deselected) — the scope for
  // "does any selected repo row carry an invalid New Price?". Omitted → derive from `data`
  // (standalone renders).
  repoCallSelectedIds?: number[];
  // The counterparty has no GMRA agreement: no repo row is callable, and the row reason says why.
  noGmraAgreement?: boolean;
  onToggleRepoExclude?: (id: number) => void;
  onSetRepoExcludedAll?: (ids: number[]) => void; // replace whole repo-deselected set (header all/none)
  // Reports whether any SELECTED repo row has an invalid New Price, so the parent can block the post.
  onRepoValidityChange?: (hasInvalidSelected: boolean) => void;
  // Repo subtab: the header select-all operates the margin-call selection (repoExcludedIds)
  // instead of the MTM run's.
  isRepoScope?: boolean;
  // LR-394 column-manager parity with TradesTable. When the sidebar Columns tab is active,
  // header drag handles appear and columns can be reordered by dragging.
  isColumnsTabActive?: boolean;
  // Right-click a header → open the column context menu (pin / hide / filter). Owned by MtmPage.
  onContextMenuColumn?: (e: React.MouseEvent, columnId: string) => void;
  // Unpin a column from its header pin indicator.
  onPinColumn?: (columnId: string, side: ColumnPinSide) => void;
  // Column ids to briefly highlight in the header (locate-from-filter-sidebar).
  highlightedColumnIds?: string[];
  // LR-386: opens the MtmDetailsDrawer (mark-to-market history) — wired to a per-row
  // micro-button in the "Last Marked" column, not to the row body (the row has a price
  // input + checkbox that own clicks already).
  onOpenDetails?: (trade: TradeItem) => void;
}

export const MtmTable: React.FC<MtmTableProps> = ({
  data,
  columns,
  pinnedLeft = [],
  pinnedRight = [],
  isLoading,
  error,
  activeCounterpartyName,
  priceOverrides,
  excludedIds,
  eligibleIds,
  onSetOverride,
  onClearOverride,
  onToggleExclude,
  onSetExcludedAll,
  onValidityChange,
  repoExcludedIds = EMPTY_REPO_EXCLUDED,
  repoCallSelectedIds,
  noGmraAgreement = false,
  onToggleRepoExclude = () => {},
  onSetRepoExcludedAll = () => {},
  onRepoValidityChange,
  isRepoScope = false,
  isColumnsTabActive = false,
  onContextMenuColumn,
  onPinColumn,
  highlightedColumnIds,
  onOpenDetails,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Local state for invalid input tracking (ephemeral display-only — never written to store)
  const [invalidIds, setInvalidIds] = useState<Set<number>>(new Set());

  // Local draft text per row, keyed by id. Decouples the <input> from the store so that
  // invalid or intermediate keystrokes render exactly as typed instead of being reverted
  // by the controlled value. Without this, a lone "0" (toward "0.07") or a leading "."
  // classifies as invalid → the store is not written → the controlled value snaps back,
  // making sub-dollar / transitional input impossible. The store override is still written
  // only for valid, non-default values; all calc/submit paths read the store, never drafts.
  const [drafts, setDrafts] = useState<Map<number, string>>(new Map());

  // Clear stale per-row UI state only when the counterparty (the selection key) changes —
  // NOT on every `data` reference change. `data` is MtmPage.poolScopedRows (a useMemo over
  // sidebar filters + pool focus + background refetch), so keying on it would wipe an
  // in-progress draft and its invalid marker the moment the trader toggles a filter or
  // focuses a pool (or a background refetch returns a new array), snapping the input back to default
  // and silently unblocking Run-MTM. Keying on the counterparty name still prevents one
  // counterparty's row ids from leaking invalid markers or draft text onto the next.
  useEffect(() => {
    setInvalidIds(new Set());
    setDrafts(new Map());
  }, [activeCounterpartyName]);

  // Surface "is any INCLUDED row invalid?" to the parent so it can block the Run-MTM submit.
  // invalidIds is local display state and an invalid row drops its override (falls back to the
  // default), so without this it would still sit in includedPositions and get marked at its
  // default price on Run — a red field that silently submits (guard the full action path).
  // Scope to eligible & not-excluded ids: an invalid repo/ineligible/excluded row never enters
  // the run, so it must not block it.
  useEffect(() => {
    const hasInvalidIncluded = [...invalidIds].some(
      (id) => eligibleIds.has(id) && !excludedIds.has(id),
    );
    onValidityChange?.(hasInvalidIncluded);
  }, [invalidIds, eligibleIds, excludedIds, onValidityChange]);

  // LR-548: repo rows SELECTED for the margin call (callable, not deselected).
  // Scoped to the PAGE's selection (`repoCallSelectedIds`); the fallback keeps standalone/unit renders
  // working, where the page prop isn't wired.
  // Without a GMRA agreement NOTHING is callable. One predicate for every consumer below.
  const repoCallScope = useMemo<RepoCallScope>(
    () => ({ noGmraAgreement }),
    [noGmraAgreement],
  );
  const isRowRepoCallable = useCallback(
    (r: TradeItem) => isRepoCallableInScope(r, repoCallScope),
    [repoCallScope],
  );

  const repoSelectedIdSet = useMemo(
    () =>
      repoCallSelectedIds
        ? new Set(repoCallSelectedIds)
        : new Set(
            data
              .filter((r) => isRowRepoCallable(r) && !repoExcludedIds.has(r.id))
              .map((r) => r.id),
          ),
    [repoCallSelectedIds, data, repoExcludedIds, isRowRepoCallable],
  );

  // Surface "does any SELECTED repo row have an invalid New Price?" so the parent can block the
  // margin-call post (mirrors onValidityChange, which guards the stock Run-MTM submit).
  useEffect(() => {
    const hasInvalidSelected = [...invalidIds].some((id) => repoSelectedIdSet.has(id));
    onRepoValidityChange?.(hasInvalidSelected);
  }, [invalidIds, repoSelectedIdSet, onRepoValidityChange]);

  // LR-538: publish the grid's edit state to the sidebar bridge store so the CSV export
  // reproduces the synthetic column totals on exactly the inputs the footer used. The
  // sidebar is a Layout sibling, so props can't reach it. Cleared on unmount so a stale
  // invalid marker can't survive a page change.
  const setMtmEditState = useMtmDataStore((s) => s.setMtmEditState);
  const resetMtmEditState = useMtmDataStore((s) => s.resetMtmEditState);
  useEffect(() => {
    setMtmEditState(invalidIds, noGmraAgreement);
  }, [invalidIds, noGmraAgreement, setMtmEditState]);
  useEffect(() => resetMtmEditState, [resetMtmEditState]);

  // useVirtualizer is now owned by VirtualizedGrid. Imports of GRID_HEADER_HEIGHT_PX
  // remain because the sticky-bar overlap math below still reads it directly.

  // ---------------------------------------------------------------------------
  // Pin grouping: left / center / right (mirrors TradesTable). The checkbox is a
  // separate fixed column, always leftmost, and is excluded from the groups.
  // ---------------------------------------------------------------------------
  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);

  // Column width including the reserve for any header icons currently present, so the
  // header stays one-line. Used for header AND body cells (and the skeleton) to keep
  // the columns aligned, mirroring how TradesTable widens pinned/filterable columns.
  const colEffectiveWidth = (col: MtmColumnDef): number => {
    const cid = getColumnId(col);
    let w = colWidth(col);
    if (isColumnsTabActive) w += HANDLE_RESERVE_PX;
    if (pinnedLeftSet.has(cid) || pinnedRightSet.has(cid)) w += PIN_RESERVE_PX;
    return w;
  };

  const { leftCols, centerCols, rightCols } = useMemo(() => {
    const left: MtmColumnDef[] = [];
    const center: MtmColumnDef[] = [];
    const right: MtmColumnDef[] = [];
    for (const col of columns) {
      const cid = getColumnId(col);
      if (pinnedLeftSet.has(cid)) left.push(col);
      else if (pinnedRightSet.has(cid)) right.push(col);
      else center.push(col);
    }
    return { leftCols: left, centerCols: center, rightCols: right };
  }, [columns, pinnedLeftSet, pinnedRightSet]);

  const hasPinnedLeft = leftCols.length > 0;
  const hasPinnedRight = rightCols.length > 0;

  // Sortable item ids for the header (all visible columns, in store order). Plain
  // column ids — the sidebar uses the same ids with a 'sidebar:' prefix, which is
  // how the shared provider tells a table drag from a sidebar drag.
  const headerColumnIds = useMemo(() => columns.map((c) => getColumnId(c)), [columns]);

  // ── LR-469: bottom column-totals footer ──
  // Totals only the rows in scope: stock L/B markable (active|matured, non-repo) and not unchecked,
  // OR — LR-548 — repo/reverse rows SELECTED for the margin call. So the footer reflects what would
  // actually be marked / called. The CSV export receives the same scoped set (kvi #1), so the screen
  // totals and the exported file reconcile. Real monetary columns total via SUMMABLE_COLUMNS on the
  // settlement basis; LR-538 adds the three override-dependent synthetic columns on top, per security
  // ccy — the repo footer is therefore stock-L/B-style over the selected repo rows. Last Marked and
  // New Price (per-share) and Gross Price Δ (a per-share move) stay blank — they do not sum.
  // base drives only the multi-currency `≈`.
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  // Reactive FX signal: recompute the ≈ roll-up when live rates land after a cold-load
  // race, instead of staying frozen on the fallback figure + ⚠ (LR-469 hooks-1).
  const fxVersion = useFxRatesStore((s) => s.version);
  const footerRows = useMemo(
    () =>
      data.filter(
        (r) =>
          (eligibleIds.has(r.id) && !excludedIds.has(r.id)) ||
          (isRowRepoCallable(r) && !repoExcludedIds.has(r.id)),
      ),
    [data, eligibleIds, excludedIds, repoExcludedIds, isRowRepoCallable],
  );
  // Real monetary columns: a whole-book pass over ~9 settlement accessors + FX, and
  // independent of the typed New Price — so it must NOT sit in the same memo as the
  // synthetic totals below, or every committed keystroke would re-run it over the
  // counterparty's entire book (the MTM page holds it all in memory, LR-546).
  const realFooterTotals = useMemo(
    () => buildFooterColumnTotals(footerRows, headerColumnIds, baseCurrency),
    // fxVersion is an intentional extra dep (unused in the body): the totals read live FX
    // through currency-service module state, so this forces a recompute when rates land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [footerRows, headerColumnIds, baseCurrency, fxVersion],
  );
  // LR-538: the three override-dependent synthetic columns (New Margin Value / Gross
  // Value Δ / Margin Value Δ) can't be SUMMABLE_COLUMNS entries — those accessors only
  // see a TradeItem and would miss the typed New Price. Pre-aggregate them per SECURITY
  // ccy (what their cells print) and push the buckets through the SAME
  // describeCurrencyTotal path, so compact/≈-roll-up/⚠ formatting is identical.
  const syntheticFooterTotals = useMemo(
    () => {
      const out: Record<string, FooterColumnTotal> = {};
      if (footerRows.length === 0) return { totals: out, deltasMixedProducts: false };
      const { byColumn, deltasMixedProducts } = buildMtmSyntheticCcyBuckets(
        footerRows,
        priceOverrides,
        repoCallScope,
        invalidIds,
      );
      const visible = new Set(headerColumnIds);
      for (const id of MTM_SYNTHETIC_SUMMABLE_IDS) {
        if (!visible.has(id)) continue;
        const byCcy = byColumn[id];
        // No contributing row (every one invalid / off-currency) → no entry, mirroring
        // buildFooterColumnTotals' empty-scope contract. describeCurrencyTotal would
        // otherwise print a base-currency 0.00 under a column of "—" cells.
        if (!byCcy || Object.keys(byCcy).length === 0) continue;
        out[id] = describeCurrencyTotal(aggregateFromBuckets(byCcy, baseCurrency));
      }
      return { totals: out, deltasMixedProducts };
    },
    // fxVersion: same live-FX recompute signal as above. `drafts` is deliberately NOT a
    // dep — it is raw display text; only a COMMITTED override (or an invalid marker)
    // may move a total.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [footerRows, headerColumnIds, baseCurrency, fxVersion, priceOverrides, repoCallScope, invalidIds],
  );
  const footerTotals = useMemo(
    () => ({ ...realFooterTotals, ...syntheticFooterTotals.totals }),
    [realFooterTotals, syntheticFooterTotals],
  );
  const hasFooterTotals = useMemo(
    () => headerColumnIds.some((id) => id in footerTotals),
    [headerColumnIds, footerTotals],
  );
  // The footer totals only what would actually be marked / called, so rows that are ON
  // SCREEN but out of scope are silently absent from every total. Unchecking is
  // self-explanatory (the trader did it); a row with no checkbox at all — pending
  // settlement, or repo of a counterparty with no GMRA agreement — is not, and that gap is
  // exactly where "the total should equal the visible column" breaks. Name it here
  // rather than leave the difference unexplained.
  const footerScopeNote = useMemo(() => {
    const outOfScope = data.length - footerRows.length;
    const scope =
      outOfScope > 0
        ? `Totals over ${footerRows.length} of ${data.length} row(s) in view — ${outOfScope} row(s) are not in the MTM run / margin call (unchecked, pending settlement, or repo with no GMRA agreement) and count towards no total`
        : `Totals over ${footerRows.length} row(s) in the MTM run / margin call`;
    // Empty Gross Value Δ / Margin Value Δ cells need their own reason: here they are blank
    // because the scope spans both products, not because nothing contributed (kvi r3 #1).
    const mixed = syntheticFooterTotals.deltasMixedProducts
      ? '; Gross Value Δ and Margin Value Δ are not totalled while stock lending/borrowing and repo are both in scope — they measure from different baselines and settle through different actions'
      : '';
    return `${scope}${mixed}; multi-currency columns shown as their ≈ ${baseCurrency} equivalent`;
  }, [data.length, footerRows.length, baseCurrency, syntheticFooterTotals.deltasMixedProducts]);

  // ---------------------------------------------------------------------------
  // Header indeterminate checkbox logic (MultiSelectDropdown pattern)
  // ---------------------------------------------------------------------------
  // LR-548: which selection the header select-all operates depends on the subtab. The repo subtab's
  // `data` holds no stock rows, so the stock `eligibleIds` set is empty there.
  const eligibleRows = data.filter((r) => eligibleIds.has(r.id));
  // Stock L/B: default-unchecked rows are MARKABLE but the header select-all never auto-includes
  // them — including one stays an explicit per-row click (audit-N4 footgun guard). The repo margin
  // call, by contrast, INCLUDES matured repo/reverse in its select-all (they still carry collateral).
  const selectableRows = isRepoScope
    ? data.filter((r) => isRowRepoCallable(r))
    : eligibleRows.filter((r) => !isMtmDefaultUnchecked(r));
  const activeExcludedIds = isRepoScope ? repoExcludedIds : excludedIds;
  const excludedSelectableCount = selectableRows.filter((r) => activeExcludedIds.has(r.id)).length;
  // LR-436 D6 (stock only): default-unchecked rows are carved out of the header select-all
  // (audit-N4 guard), so one the trader manually ticked IN is invisible from the header tri-state.
  // Count those manual inclusions for a small "+N" indicator. Repo keeps matured in → no carve-out.
  const includedDefaultUncheckedCount = isRepoScope
    ? 0
    : eligibleRows.filter((r) => isMtmDefaultUnchecked(r) && !excludedIds.has(r.id)).length;
  // Guard the empty case (e.g. a repo subtab with nothing callable): without
  // `hasSelectable`, `excludedSelectableCount === 0` is trivially true and the
  // header box would render "all included / checked" over a view with nothing
  // to mark.
  const hasSelectable = selectableRows.length > 0;
  const allIncluded = hasSelectable && excludedSelectableCount === 0;
  const noneIncluded = hasSelectable && excludedSelectableCount === selectableRows.length;
  const someExcluded = !allIncluded && !noneIncluded && hasSelectable;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someExcluded;
    }
    // Depend on hasSelectable too (LR-208 r6 LOW): the header checkbox is only
    // rendered once there are selectable rows, so when the table transitions
    // empty/loading → populated the checkbox remounts. Without hasSelectable the
    // effect wouldn't re-run if `someExcluded` was unchanged, leaving the fresh
    // checkbox with a stale indeterminate=false.
  }, [someExcluded, hasSelectable]);

  const onToggleAll = () => {
    // Derive the NEXT excluded set from the CURRENT store set, never by replacing
    // it with a set built from the visible subset alone (LR-208 r6 H3). The old
    // "replace from `data`" wiped any excluded id NOT currently visible — most
    // importantly a default-excluded matured row (or a manual exclusion) hidden
    // by an active sidebar filter or pool focus — silently re-including it in the next
    // Run MTM once the filter cleared. The header toggle only flips the VISIBLE
    // non-matured rows; matured rows and filter-hidden rows keep their state.
    const visibleSelectableIds = selectableRows.map((r) => r.id);
    // LR-548: target the subtab's deselection set (stock excludedIds vs repoExcludedIds).
    const setExcludedAll = isRepoScope ? onSetRepoExcludedAll : onSetExcludedAll;
    if (allIncluded) {
      // Exclude all visible selectable; keep every other exclusion (hidden + matured).
      setExcludedAll([...activeExcludedIds, ...visibleSelectableIds]);
    } else {
      // Include all visible selectable; leave every other exclusion untouched.
      const visible = new Set(visibleSelectableIds);
      setExcludedAll([...activeExcludedIds].filter((id) => !visible.has(id)));
    }
  };

  // ---------------------------------------------------------------------------
  // Loading skeleton sizing (mirrors the Trades GenericTableSkeleton)
  // ---------------------------------------------------------------------------
  // Show the skeleton only on the first load (no rows yet), not on a background refetch.
  const showSkeleton = isLoading && data.length === 0;

  // Per-column widths for the skeleton bars, in the same visual order as the real
  // header: left-pinned, then center, then right-pinned (checkbox handled separately).
  const skeletonColWidths = useMemo(
    () => [...leftCols, ...centerCols, ...rightCols].map((c) => colEffectiveWidth(c)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- colEffectiveWidth is a render-local closure; its inputs (pins via the col groups, isColumnsTabActive) are listed explicitly
    [leftCols, centerCols, rightCols, isColumnsTabActive],
  );

  // Fill the visible height with rows (computed from the scroll container, minus the 41px
  // header). Floor at 8 so it never looks sparse and stays non-empty when the height is
  // unmeasurable (e.g. jsdom in tests).
  const [skeletonRowCount, setSkeletonRowCount] = useState(12);
  useLayoutEffect(() => {
    if (!showSkeleton) return;
    const el = parentRef.current;
    if (!el) return;
    setSkeletonRowCount(Math.max(8, Math.ceil((el.clientHeight - GRID_HEADER_HEIGHT_PX) / GRID_ROW_HEIGHT_PX)));
  }, [showSkeleton]);

  const renderCell = (
    column: MtmColumnDef,
    item: TradeItem,
    rowIndex: number,
  ): React.ReactNode => {
    const columnId = getColumnId(column);
    // A column with an accessor function shows what it derives, not the raw field.
    const cellValue = 'accessorFn' in column
      ? column.accessorFn(item, rowIndex)
      : item[columnId as keyof TradeItem];
    return column.cell && typeof column.cell === 'function'
      ? column.cell({
          getValue: () => cellValue,
          row: { original: item, index: rowIndex },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as unknown as CellContext<TradeItem, any>)
      : String(cellValue ?? '');
  };

  // ---------------------------------------------------------------------------
  // Header cell renderer (one per visible column). Synthetic ids get their bespoke
  // labels/alignment; real columns use meta.align-aware right-alignment.
  // ---------------------------------------------------------------------------
  const renderHeaderCell = (col: MtmColumnDef): React.ReactNode => {
    const id = getColumnId(col);
    const w = colEffectiveWidth(col);
    let label: React.ReactNode;
    let align: 'left' | 'right';
    switch (id) {
      case MTM_SYNTHETIC_LAST_MARKED:
        label = 'Last Marked';
        align = 'right';
        break;
      case MTM_SYNTHETIC_NEW_PRICE:
        label = 'New Price';
        align = 'left';
        break;
      case MTM_SYNTHETIC_DELTA:
        // Per-share price move vs the baseline. Sign is the raw direction of the move
        // — NOT P&L-directioned (see Gross Value Δ for the directioned market-value
        // delta). Baseline differs by product: stock L/B move off the last mark
        // (mtmBaselinePriceRaw), repo/reverse off the fixed booking price (LR-548).
        label = (
          <span
            className="inline-flex items-center justify-end gap-1 whitespace-nowrap"
            title="Per-share price move if the New Price is applied: New Price − baseline. Baseline is the last marked price (or the booking price for never-marked positions); for repo / reverse repo it is always the booking gross price, since repos are never MtM-repriced. Sign reflects the price move itself, not P&L direction. See Gross Value Δ for the directioned market-value impact."
          >
            Gross Price Δ
            <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
          </span>
        );
        align = 'right';
        break;
      case MTM_SYNTHETIC_NEW_MARGIN:
        label = (
          <span
            className="inline-flex items-center justify-end gap-1 whitespace-nowrap"
            title="Required collateral if the New Price is applied: Quantity × New Price × Margin%. Appears once you enter a New Price; coloured green/red vs the position's current margin."
          >
            New Margin Value
            <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
          </span>
        );
        align = 'right';
        break;
      case MTM_SYNTHETIC_VALUE_DELTA:
        // Directioned Δ over the unmargined (gross) notional, matching History
        // `Gross Value Δ`. Stock L/B: pnlDirection × (new − moving baseline) × qty,
        // lend +, borrow −. Repo/reverse are not MTM'd (LR-385) so pnlDirection is 0
        // for them — LR-548 routes those rows through computeRepoValueDelta instead,
        // directioned by repoExposureDirection off the fixed booking price.
        label = (
          <span
            className="inline-flex items-center justify-end gap-1 whitespace-nowrap"
            title="Directioned gross market-value Δ if the New Price is applied: direction × (New Price − baseline) × Quantity. Lend positive when price↑, borrow negative when price↑. For repo / reverse repo the baseline is the booking gross price and the direction is the cash-pool exposure direction (repo +, reverse repo −). Same formula as the History Gross Value Δ column — the figure shown here equals what the History row will show after Run MTM."
          >
            Gross Value Δ
            <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
          </span>
        );
        align = 'right';
        break;
      case MTM_SYNTHETIC_COLLATERAL_DELTA:
        // Directioned MARGINED Δ (matches History `Margin Value Δ`): Gross Value Δ
        // scaled by this position's margin — the change in required collateral /
        // margin-call impact. Equals Gross Value Δ at margin 100%, differs otherwise
        // (e.g. 110%). For repo/reverse this is the PRICE part of the cash-pool margin
        // call's per-line exposure (the backend's `clean_exposure`, LR-548) — under
        // Method B the call also carries unpaid accrued repo interest, which is not
        // derivable from the row and stays out of this column (LR-579).
        label = (
          <span
            className="inline-flex items-center justify-end gap-1 whitespace-nowrap"
            title="Directioned margin-value Δ if the New Price is applied: direction × (New Price − baseline) × Quantity × Margin%. The margined (collateral-call) impact — equals Gross Value Δ scaled by this position's margin. For repo / reverse repo this is the PRICE move only: under Method B the margin call's exposure also includes unpaid accrued repo interest, so this column (and its total) sit below the bar's Current Exposure by that amount. Same formula as the History Margin Value Δ column — the figure shown here equals what the History row will show after Run MTM."
          >
            Margin Value Δ
            <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
          </span>
        );
        align = 'right';
        break;
      default:
        label = typeof col.header === 'string' ? col.header : id;
        align = columnAlign(col);
    }
    const pinSide: ColumnPinSide = pinnedLeftSet.has(id) ? 'left' : pinnedRightSet.has(id) ? 'right' : null;
    return (
      <MtmSortableHeader
        key={id}
        id={id}
        width={w}
        align={align}
        showHandle={isColumnsTabActive}
        isHighlighted={highlightedColumnIds?.includes(id) ?? false}
        pinSide={pinSide}
        onUnpin={onPinColumn ? () => onPinColumn(id, null) : undefined}
        onContextMenu={onContextMenuColumn ? (e) => onContextMenuColumn(e, id) : undefined}
      >
        {label}
      </MtmSortableHeader>
    );
  };

  // Per-row computed context shared by the synthetic-cell renderers.
  interface RowCtx {
    defaultPrice: string;
    currency: string;
    isRepoType: boolean;
    inputValue: string;
    isInvalid: boolean;
    handlePriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    deltaNode: React.ReactNode;
    newMarginNode: React.ReactNode;
    valueDeltaNode: React.ReactNode;
    collateralDeltaNode: React.ReactNode;
  }

  // Body cell renderer (one per visible column). Branches on eligibility: eligible
  // rows get the editable price block; ineligible (pending-settlement) rows get the
  // static gray block. Real columns render via the column def's cell.
  const renderBodyCell = (
    col: MtmColumnDef,
    item: TradeItem,
    rowIndex: number,
    isEligible: boolean,
    ctx: RowCtx,
  ): React.ReactNode => {
    const id = getColumnId(col);
    const w = colEffectiveWidth(col);
    const sizeStyle = { width: w, minWidth: w };

    if (SYNTHETIC_ID_SET.has(id)) {
      // ── Synthetic price-block cells ──
      if (!isEligible) {
        switch (id) {
          case MTM_SYNTHETIC_LAST_MARKED:
            // Mark-history micro-button is rendered here too, so a pending row's prior
            // marks (made while it was active) stay reachable.
            return (
              <div key={id} style={sizeStyle} className="px-2 font-mono tabular-nums text-sm text-right">
                {item.latest_mark_price_raw != null ? (
                  <span className="inline-flex items-center justify-end gap-1">
                    {onOpenDetails && (
                      <button
                        type="button"
                        onClick={() => onOpenDetails(item)}
                        className="rounded p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        title="Mark history"
                        aria-label={`View mark history for position ${item.id}`}
                      >
                        <ClockIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <span className="text-gray-400">{formatMarkPrice(item.latest_mark_price_raw)}</span>
                    <span className="text-xs text-gray-400">{ctx.currency}</span>
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            );
          case MTM_SYNTHETIC_NEW_PRICE:
            return (
              <div key={id} style={sizeStyle} className="px-2 text-sm font-mono tabular-nums text-gray-400">
                {ctx.defaultPrice}
                <span className="ml-1 text-xs text-gray-400">{ctx.currency}</span>
              </div>
            );
          case MTM_SYNTHETIC_DELTA:
          case MTM_SYNTHETIC_NEW_MARGIN:
          case MTM_SYNTHETIC_VALUE_DELTA:
          case MTM_SYNTHETIC_COLLATERAL_DELTA:
            // Derived cells (Gross Price Δ / New Margin Value / Gross Value Δ / Margin Value Δ) all
            // blank to a muted em-dash on ineligible (pending-settlement etc.) rows.
            return <div key={id} style={sizeStyle} className="px-2 text-sm text-gray-300 text-right">—</div>;
        }
      }
      // Eligible synthetic cells
      switch (id) {
        case MTM_SYNTHETIC_LAST_MARKED:
          // Reads latest_mark_price_raw (the genuine last MTM mark, null when never marked)
          // — NOT last_marked_price, which the backend masks with current_market_price and
          // would mislabel an unmarked row as "marked". When marked, a micro-button to the
          // left of the price opens the MtmDetailsDrawer (LR-386 mark history).
          return (
            <div key={id} style={sizeStyle} className="px-2 font-mono tabular-nums text-sm text-right">
              {item.latest_mark_price_raw != null ? (
                <span className="inline-flex items-center justify-end gap-1">
                  {onOpenDetails && (
                    <button
                      type="button"
                      onClick={() => onOpenDetails(item)}
                      className="rounded p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Mark history"
                      aria-label={`View mark history for position ${item.id}`}
                    >
                      <ClockIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span className="text-gray-900">{formatMarkPrice(item.latest_mark_price_raw)}</span>
                  <span className="text-xs text-gray-500">{ctx.currency}</span>
                </span>
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </div>
          );
        case MTM_SYNTHETIC_NEW_PRICE:
          return (
            <div key={id} style={sizeStyle} className="px-2">
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={ctx.inputValue}
                  onChange={ctx.handlePriceChange}
                  placeholder={ctx.defaultPrice}
                  title={
                    ctx.isRepoType
                      ? `Leaving this empty values the leg at the default ${ctx.defaultPrice} (COB / T-1 close, or booking price if COB is unavailable). Enter a New Price to re-value it for the margin call (LR-548).`
                      : `Leaving this empty marks at the default ${ctx.defaultPrice} (COB / T-1 close, or booking price if COB is unavailable — LR-393).`
                  }
                  className={clsx(
                    'h-7 min-w-0 flex-1 rounded border px-2 text-sm font-mono tabular-nums bg-white',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0',
                    ctx.isInvalid
                      ? 'border-red-400 ring-2 ring-red-200 ring-offset-0'
                      : 'border-gray-300 focus:border-blue-500',
                  )}
                  aria-label={`New price for position ${item.id}`}
                  aria-invalid={ctx.isInvalid ? true : undefined}
                />
                <span className="shrink-0 text-xs text-gray-500">{ctx.currency}</span>
              </div>
            </div>
          );
        case MTM_SYNTHETIC_DELTA:
          return (
            <div key={id} style={sizeStyle} className="px-2 text-sm font-mono tabular-nums text-right">
              {ctx.deltaNode}
            </div>
          );
        case MTM_SYNTHETIC_NEW_MARGIN:
          return (
            <div key={id} style={sizeStyle} className="px-2 text-sm font-mono tabular-nums text-right">
              {ctx.newMarginNode}
            </div>
          );
        case MTM_SYNTHETIC_VALUE_DELTA:
          return (
            <div key={id} style={sizeStyle} className="px-2 text-sm font-mono tabular-nums text-right">
              {ctx.valueDeltaNode}
            </div>
          );
        case MTM_SYNTHETIC_COLLATERAL_DELTA:
          return (
            <div key={id} style={sizeStyle} className="px-2 text-sm font-mono tabular-nums text-right">
              {ctx.collateralDeltaNode}
            </div>
          );
      }
    }

    // ── LR-388: Quantity cell with a pending partial recall/return ──
    // MTM marks the REMAINING shape, so show that here (not the full booked quantity) plus a
    // "pending N recall" pill. effectiveQuantity is the same source the CV / margin / exposure
    // cells use, so the displayed quantity and those figures can never disagree.
    if (id === 'quantity' && (item.pending_recall_quantity ?? 0) > 0) {
      const recalled = item.pending_recall_quantity ?? 0;
      // borrow → we RETURN borrowed shares; lend → we RECALL lent shares.
      const verb = item.trade_type === 'borrow' ? 'return' : 'recall';
      // Full detail in the tooltip; the pill stays compact + truncates so it can NEVER
      // overflow the narrow (90px) Quantity column into the neighbouring cell.
      const tip = `Booked ${item.quantity.toLocaleString('en-US')} · pending ${recalled.toLocaleString('en-US')} ${verb}`;
      return (
        <div
          key={id}
          style={sizeStyle}
          className={clsx('px-2 text-sm', isEligible ? 'text-gray-900' : 'text-gray-400')}
        >
          <div className="flex min-w-0 flex-col items-end gap-0.5" title={tip}>
            <span className="font-medium tabular-nums">
              {effectiveQuantity(item).toLocaleString('en-US')}
            </span>
            <span className="max-w-full truncate rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
              {verb} {recalled.toLocaleString('en-US')}
            </span>
          </div>
        </div>
      );
    }

    // ── Real trades column cell ──
    return (
      <div
        key={id}
        style={sizeStyle}
        className={clsx('px-2 text-sm', isEligible ? 'text-gray-900' : 'text-gray-400')}
      >
        {renderCell(col, item, rowIndex)}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // ERROR state
  // ---------------------------------------------------------------------------
  if (error != null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-300" />
          <p className="text-sm font-semibold text-gray-700">Failed to load trades</p>
          <p className="text-xs text-gray-400">Check your connection and refresh the page.</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // EMPTY state
  // ---------------------------------------------------------------------------
  if (!isLoading && !error && data.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <DocumentMagnifyingGlassIcon className="h-10 w-10 text-gray-300" />
          <p className="text-sm font-semibold text-gray-500">No open trades</p>
          <p className="text-xs text-gray-400">
            {activeCounterpartyName} has no open positions.
          </p>
        </div>
      </div>
    );
  }

  // Header checkbox cell (fixed leftmost, sticky with the left zone).
  const headerCheckbox = (
    <div
      style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH }}
      className="relative flex items-center justify-center px-2"
    >
      <input
        type="checkbox"
        ref={headerCheckboxRef}
        checked={allIncluded}
        onChange={onToggleAll}
        disabled={!hasSelectable}
        className={clsx(
          'h-4 w-4 accent-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          hasSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-40',
        )}
        aria-label={
          isRepoScope
            ? 'Include all selectable positions in the margin call'
            : 'Include all selectable positions in MTM run'
        }
        title={
          isRepoScope
            ? 'Includes all active and matured repo / reverse repo rows in the margin call.'
            : 'Includes all active rows; matured and partially settled rows stay excluded (tick them per-row to include).'
        }
      />
      {/* `role="img"` (LR-550): a bare span is `generic`, which does not support naming, so
          the aria-label below was not exposed at all. The label already states the count, so
          making this a leaf hides nothing. No `title` — `pointer-events-none` means it is
          never a hover target, so a native tooltip here could never fire. */}
      {includedDefaultUncheckedCount > 0 && (
        <span
          className="pointer-events-none absolute -right-1 -top-1 rounded-full bg-lr-warn px-1 text-[9px] font-bold leading-tight text-white"
          role="img"
          aria-label={`${includedDefaultUncheckedCount} ${includedDefaultUncheckedCount === 1 ? 'row' : 'rows'} manually included in this MTM run (matured or partially settled — excluded by default)`}
        >
          +{includedDefaultUncheckedCount}
        </span>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // DATA state — virtualized interactive table
  // ---------------------------------------------------------------------------
  const headerNode = (
    <SortableContext items={headerColumnIds} strategy={noDisplacementStrategy}>
      {/* Left sticky zone: checkbox (always) + left-pinned headers */}
      <div
        className={clsx(
          'sticky left-0 z-[12] flex items-center self-stretch bg-gray-50 pl-4',
          hasPinnedLeft ? 'pr-2 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]' : 'pr-0',
        )}
      >
        {headerCheckbox}
        {leftCols.map((col) => renderHeaderCell(col))}
      </div>

      {/* Center headers */}
      <div className={clsx('flex items-center self-stretch pl-2', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {centerCols.map((col) => renderHeaderCell(col))}
      </div>

      {/* Right sticky zone */}
      {hasPinnedRight && (
        <div className="sticky right-0 z-[12] flex items-center self-stretch bg-gray-50 pl-2 pr-4 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]">
          {rightCols.map((col) => renderHeaderCell(col))}
        </div>
      )}
    </SortableContext>
  );

  // LR-469: footer cell per visible column — same colEffectiveWidth + px-2 padding as the
  // body cells so totals line up under their columns. Real summable columns show their
  // total (right-aligned, mono); synthetic price-block columns render blank.
  const renderFooterCell = (col: MtmColumnDef): React.ReactNode => {
    const id = getColumnId(col);
    const w = colEffectiveWidth(col);
    const total = footerTotals[id];
    const align = columnAlign(col);
    return (
      <div
        key={id}
        data-footer-col={id}
        style={{ width: w, minWidth: w }}
        className={clsx('px-2 flex items-center whitespace-nowrap', align === 'right' ? 'justify-end' : 'justify-start')}
      >
        {total ? (
          <span className="truncate font-mono tabular-nums text-sm text-gray-900" title={total.title || undefined}>
            {total.text}
            {total.approx && (
              <span
                className="ml-0.5 text-amber-600"
                role="img"
                aria-label={approxRateNote(total.approxCurrencies)}
                title={approxRateNote(total.approxCurrencies)}
              >
                ⚠
              </span>
            )}
          </span>
        ) : null}
      </div>
    );
  };

  // Sticky bottom totals row (LR-469). Mirrors headerNode's zones (checkbox spacer +
  // left/center/right) so totals align with their columns on both scroll axes.
  // Rendered only when at least one summable column is visible AND there is at least
  // one in-scope row to total.
  const footerNode = hasFooterTotals ? (
    <div
      className="flex items-stretch border-t border-gray-200 bg-gray-50 text-sm font-medium text-gray-900"
      style={GRID_HEADER_HEIGHT_STYLE}
      data-mtm-table-footer
    >
      {/* "Total" row label — pinned to the left edge so it never scrolls out of view
          horizontally. The 40px checkbox column is too narrow for the word, so the
          negative margin cancels the label's own width to OVERLAY the checkbox + the
          leftmost blank cells instead of shifting every column. z-20 keeps it above
          the pinned-zone cells (z-12). */}
      <div
        data-mtm-total-label
        className="sticky left-0 z-20 flex items-center self-stretch bg-gray-50 pl-4 pr-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
        style={{ width: `${TOTAL_LABEL_WIDTH}px`, marginRight: `-${TOTAL_LABEL_WIDTH}px` }}
        title={footerScopeNote}
      >
        Total
      </div>
      <div
        className={clsx(
          'sticky left-0 z-[12] flex items-center self-stretch bg-gray-50 pl-4',
          hasPinnedLeft ? 'pr-2 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]' : 'pr-0',
        )}
      >
        {/* Spacer matching the checkbox column so the totals line up under their headers. */}
        <div style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH }} className="px-2" />
        {leftCols.map((col) => renderFooterCell(col))}
      </div>
      <div className={clsx('flex items-center self-stretch pl-2', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {centerCols.map((col) => renderFooterCell(col))}
      </div>
      {hasPinnedRight && (
        <div className="sticky right-0 z-[12] flex items-center self-stretch bg-gray-50 pl-2 pr-4 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]">
          {rightCols.map((col) => renderFooterCell(col))}
        </div>
      )}
    </div>
  ) : undefined;

  const skeletonBody = showSkeleton ? (
    <div className="flex-1 animate-pulse" aria-hidden="true">
      {Array.from({ length: skeletonRowCount }).map((_, r) => (
        <div
          key={r}
          data-testid="mtm-skeleton-row"
          className="flex items-center border-b border-gray-100 pl-4 pr-4"
          style={GRID_ROW_HEIGHT_STYLE}
        >
          {/* Checkbox column spacer — matches the header/data checkbox cell */}
          <div style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH }} className="px-2" />
          {skeletonColWidths.map((w, i) => (
            <div key={i} style={{ width: w, minWidth: w }} className="px-2">
              <div
                className="h-3.5 rounded bg-gray-200"
                style={{ width: Math.round(w * 0.7) }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <VirtualizedGrid<TradeItem>
      rows={data}
      estimateRowHeight={GRID_ROW_HEIGHT_PX}
      overscan={5}
      getRowKey={(row, index) => row.id ?? index}
      scrollContainerRef={parentRef}
      scrollContainerProps={{ 'data-mtm-table-scroll-container': true } as React.HTMLAttributes<HTMLDivElement>}
      header={headerNode}
      headerClassName="shrink-0"
      headerStyle={GRID_HEADER_HEIGHT_STYLE}
      bodyOverride={skeletonBody}
      footer={footerNode}
      renderRow={({ row: item, index, virtualRow, style }) => {

            // Repo / reverse repo are NOT repriced on the MTM page — they are handled by a
            // per-counterparty cash-pool margin call (LR-385). Guard the New Price input so a
            // trader cannot submit a meaningless mark on a repo row.
            const isRepoType = item.trade_type === 'repo' || item.trade_type === 'reverse_repo';
            // A repo row that cannot be called (no GMRA agreement) is treated like a pending-settlement
            // row — greyed, checkbox disabled, no New Price input. Stock rows are unaffected.
            const isEligible =
              isMtmEligibleRow(item) &&
              (!isRepoType || isRowRepoCallable(item));
            // Why the checkbox is off. The status test is per-product: repo eligibility stayed on the
            // RAW status, so a partially settled FOP repo would otherwise pass it.
            const statusIneligible = isRepoType
              ? !isEligibleStatus(item.status)
              : !isMtmEligibleRow(item);
            const ineligibleReason = statusIneligible
              ? 'Pending settlement — not eligible for MTM'
              : 'This counterparty has no GMRA agreement, so its repo book has no currency to be ' +
                'margined in. Add the agreement in the counterparty registry.';
            const defaultPrice = resolveDefaultPrice(item);
            const currency = item.currency;
            const override = priceOverrides.get(item.id);
            const isOverridden = override !== undefined;
            const draft = drafts.get(item.id);
            // Show the raw draft once the user has typed (even '' or invalid text);
            // fall back to the committed override, then the resolved default.
            const inputValue = draft ?? override ?? defaultPrice;
            const isInvalid = invalidIds.has(item.id);

            const clearInvalid = (id: number) =>
              setInvalidIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
              });

            // Handler for New Price input changes (value-based override detection).
            // Always record the raw text as the draft so the field reflects what was
            // typed; classification then drives the store + invalid marker.
            const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              setDrafts((prev) => new Map(prev).set(item.id, raw));
              const cls = classifyPriceInput(raw, defaultPrice);
              if (cls.kind === 'override') {
                onSetOverride(item.id, cls.value);
                clearInvalid(item.id);
              } else if (cls.kind === 'default') {
                onClearOverride(item.id);
                clearInvalid(item.id);
              } else if (cls.kind === 'invalid') {
                // Mark invalid in local display state AND drop any previously-committed
                // override for this row. Otherwise a stale valid override would keep
                // feeding the aggregate bar and the Run-MTM submit while the field shows
                // a red, invalid value — i.e. the run would mark a price the user can no
                // longer see. With the override cleared, an invalid row falls back to its
                // default (last_marked) for all calc/submit until a valid value is typed.
                onClearOverride(item.id);
                setInvalidIds((prev) => new Set(prev).add(item.id));
              } else {
                // 'empty' — clear invalid state, clear override
                onClearOverride(item.id);
                clearInvalid(item.id);
              }
            };

            let deltaNode: React.ReactNode = <span className="text-gray-300">—</span>;
            let newMarginNode: React.ReactNode = <span className="text-gray-300">—</span>;
            let valueDeltaNode: React.ReactNode = <span className="text-gray-300">—</span>;
            let collateralDeltaNode: React.ReactNode = <span className="text-gray-300">—</span>;

            // Δ + New Margin Value render for EVERY eligible non-repo non-invalid row —
            // not just overridden ones (LR-413). The effective price comes from the same
            // getMtmSubmitPrice chain the bar/dialog/marks[] use (override ?? cob ?? gross),
            // and the baseline from mtmBaselinePriceRaw (last_mark ?? gross) — so a COB-
            // prefilled row shows its proposed Δ vs the moving baseline without the user
            // having to type. Both branches read mtm.effectivePrice (single source of truth)
            // instead of re-resolving getMtmSubmitPrice separately for the Δ math.
            if (isEligible && !isRepoType && !isInvalid) {
              const mtm = computeMtmRow(item, override);
              const effectivePriceNum = Number(mtm.effectivePrice);
              const baselinePriceNum = Number(mtmBaselinePriceRaw(item));

              if (!Number.isNaN(effectivePriceNum) && !Number.isNaN(baselinePriceNum)) {
                const { abs, pct } = computeDelta(effectivePriceNum, baselinePriceNum);
                const absDir = signedDirection(abs);
                // pct shares abs's sign in the normal case (baseline > 0); when baseline = 0
                // computeDelta guards pct to 0, which independently formats as '+0.00%'. No
                // need for a second signedDirection() — the row colour is owned by absDir.
                const pctSign = pct >= 0 ? '+' : '−'; // U+2212 minus sign
                // Currency suffix on the absolute price delta (a per-share price move in
                // item.currency), muted like the other price cells; the % stays alongside.
                deltaNode = (
                  <span className={absDir.color}>
                    {`${absDir.sign}${Math.abs(abs).toFixed(2)}`}
                    <span className="text-xs text-gray-500">{` ${currency}`}</span>
                    {` (${pctSign}${Math.abs(pct).toFixed(2)}%)`}
                  </span>
                );

                const newMarginNum = Number(mtm.recomputedMargin);
                // Colour against the baseline margined VALUE, not item.margin (a PERCENT —
                // unit mismatch coloured every row green).
                const baselineMarginNum = baselineMarginedValue(item);
                const marginDir = signedDirection(newMarginNum - baselineMarginNum);
                const marginDisplay = newMarginNum.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                newMarginNode = (
                  <>
                    <span className={marginDir.color}>{marginDisplay}</span>
                    <span className="ml-1 text-xs text-gray-500">{currency}</span>
                  </>
                );

                // Gross Value Δ = directioned P&L over the unmargined notional. Mirrors
                // History MtmRunMarkSerializer.delta_value so the figure shown here
                // before Run MTM equals the figure History shows after commit.
                const vd = computeValueDelta(item, effectivePriceNum, baselinePriceNum);
                const vdDir = signedDirection(vd);
                const vdDisplay = Math.abs(vd).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                valueDeltaNode = (
                  <>
                    <span className={vdDir.color}>{`${vdDir.sign}${vdDisplay}`}</span>
                    <span className="ml-1 text-xs text-gray-500">{currency}</span>
                  </>
                );

                // Margin Value Δ = directioned MARGINED Δ (Gross Value Δ scaled by margin).
                // Mirrors History MtmRunMarkSerializer.delta_collateral so the preview
                // figure equals what History shows after commit. Equals Gross Value Δ at
                // margin 100%, differs otherwise (the "margin price Δ × qty" the desk wants).
                // Guard on the COMPUTED result, not the raw margin input. computeCollateralDelta
                // coerces margin arithmetically (t.margin / 100), so cd is a clean number whether
                // margin arrives as the transformed number (transformPositionToTradeItem) or a raw
                // API string — and NaN only for a genuinely corrupt margin, where the cell then
                // keeps the em-dash placeholder instead of rendering "−NaN". (Guarding
                // Number.isFinite(item.margin) directly would wrongly blank the column if margin
                // were ever a string — see kvi review LR-461 #1.)
                const cd = computeCollateralDelta(item, effectivePriceNum, baselinePriceNum);
                if (Number.isFinite(cd)) {
                  const cdDir = signedDirection(cd);
                  const cdDisplay = Math.abs(cd).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                  collateralDeltaNode = (
                    <>
                      <span className={cdDir.color}>{`${cdDir.sign}${cdDisplay}`}</span>
                      <span className="ml-1 text-xs text-gray-500">{currency}</span>
                    </>
                  );
                }
              }
            } else if (isEligible && isRepoType && !isInvalid) {
              // LR-548: repo/reverse deltas. Measured from the FIXED gross (booking) price — repo is
              // never MTM-repriced (LR-385) — and directioned by repoExposureDirection (repo +1 /
              // reverse_repo −1), NOT the repo-neutral pnlDirection. New Margin Value reuses the
              // direction-agnostic recomputedMargin; the margin-value delta (Margin Value Δ) IS the
              // signed per-line exposure the backend returns.
              const mtm = computeMtmRow(item, override);
              const effectivePriceNum = Number(mtm.effectivePrice);
              const grossPriceNum = Number(item.gross_price_raw);

              if (!Number.isNaN(effectivePriceNum) && !Number.isNaN(grossPriceNum)) {
                // Gross Price Δ = new − gross (from the booking price, not a moving baseline).
                const { abs, pct } = computeDelta(effectivePriceNum, grossPriceNum);
                const absDir = signedDirection(abs);
                const pctSign = pct >= 0 ? '+' : '−';
                deltaNode = (
                  <span className={absDir.color}>
                    {`${absDir.sign}${Math.abs(abs).toFixed(2)}`}
                    <span className="text-xs text-gray-500">{` ${currency}`}</span>
                    {` (${pctSign}${Math.abs(pct).toFixed(2)}%)`}
                  </span>
                );

                // New Margin Value = qty × new × md (market_value). Colour by the exposure sign
                // (repo margin-value delta) so green = margin moved in our favour.
                const newMarginNum = Number(mtm.recomputedMargin);
                const repoMvDelta = computeRepoMarginValueDelta(item, effectivePriceNum, grossPriceNum);
                const marginDir = signedDirection(repoMvDelta);
                const marginDisplay = newMarginNum.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                newMarginNode = (
                  <>
                    <span className={marginDir.color}>{marginDisplay}</span>
                    <span className="ml-1 text-xs text-gray-500">{currency}</span>
                  </>
                );

                // Gross Value Δ = directioned GROSS (unmargined) Δ from the booking price.
                const vd = computeRepoValueDelta(item, effectivePriceNum, grossPriceNum);
                const vdDir = signedDirection(vd);
                const vdDisplay = Math.abs(vd).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                valueDeltaNode = (
                  <>
                    <span className={vdDir.color}>{`${vdDir.sign}${vdDisplay}`}</span>
                    <span className="ml-1 text-xs text-gray-500">{currency}</span>
                  </>
                );

                // Margin Value Δ = the signed per-line exposure: direction × qty × md × (new − gross).
                // Its sign must equal the preview line's `exposure` (LR-548 acceptance 5).
                if (Number.isFinite(repoMvDelta)) {
                  const cdDir = signedDirection(repoMvDelta);
                  const cdDisplay = Math.abs(repoMvDelta).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                  collateralDeltaNode = (
                    <>
                      <span className={cdDir.color}>{`${cdDir.sign}${cdDisplay}`}</span>
                      <span className="ml-1 text-xs text-gray-500">{currency}</span>
                    </>
                  );
                }
              }
            }

            const ctx: RowCtx = {
              defaultPrice,
              currency,
              isRepoType,
              inputValue,
              isInvalid,
              handlePriceChange,
              deltaNode,
              newMarginNode,
              valueDeltaNode,
              collateralDeltaNode,
            };

            // Checkbox cell — eligible rows toggle their family's selection: stock L/B → excludedIds
            // (the MTM run), repo/reverse → repoExcludedIds (the cash-pool margin call, LR-548).
            // Ineligible (pending-settlement) rows are disabled in either family.
            const checkboxCell = isEligible ? (
              <div style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH }} className="flex items-center justify-center px-2">
                <input
                  type="checkbox"
                  checked={
                    isRepoType ? !repoExcludedIds.has(item.id) : !excludedIds.has(item.id)
                  }
                  onChange={() =>
                    isRepoType ? onToggleRepoExclude(item.id) : onToggleExclude(item.id)
                  }
                  title={
                    isRepoType
                      ? 'Include this repo / reverse repo position in the cash-pool margin call (LR-548).'
                      : undefined
                  }
                  className="h-4 w-4 cursor-pointer accent-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  aria-label={
                    isRepoType
                      ? `Include position ${item.id} in the margin call`
                      : `Include position ${item.id} in MTM run`
                  }
                />
              </div>
            ) : (
              <div style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH }} className="flex items-center justify-center px-2">
                <input
                  type="checkbox"
                  disabled
                  checked={false}
                  className="h-4 w-4 opacity-40 cursor-not-allowed"
                  title={ineligibleReason}
                  aria-label={`Position ${item.id} not eligible — ${ineligibleReason}`}
                  aria-disabled="true"
                />
              </div>
            );

            const rowBg = isEligible
              ? (isOverridden ? 'bg-amber-50 hover:bg-amber-100' : 'bg-white hover:bg-gray-50')
              : 'bg-gray-50';
            // Sticky-zone backgrounds must be opaque so center cells slide under them on
            // horizontal scroll. Match the row background (amber/white/gray) per state.
            const stickyBg = isEligible
              ? (isOverridden ? 'bg-amber-50' : 'bg-white')
              : 'bg-gray-50';

            return (
              <div key={virtualRow?.key ?? item.id ?? index} style={style}>
                <div
                  role="row"
                  className={clsx('relative flex items-center border-b border-gray-100 transition-colors', rowBg)}
                  style={GRID_ROW_HEIGHT_STYLE}
                >
                  {/* Left sticky zone: checkbox (always) + left-pinned cells */}
                  <div
                    className={clsx(
                      'sticky left-0 z-[2] flex items-center self-stretch pl-4',
                      stickyBg,
                      hasPinnedLeft ? 'pr-2 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]' : 'pr-0',
                    )}
                  >
                    {checkboxCell}
                    {leftCols.map((col) => renderBodyCell(col, item, index, isEligible, ctx))}
                  </div>

                  {/* Center cells */}
                  <div className={clsx('flex items-center self-stretch pl-2', hasPinnedRight ? 'pr-2' : 'pr-4')}>
                    {centerCols.map((col) => renderBodyCell(col, item, index, isEligible, ctx))}
                  </div>

                  {/* Right sticky zone */}
                  {hasPinnedRight && (
                    <div
                      className={clsx(
                        'sticky right-0 z-[2] flex items-center self-stretch pl-2 pr-4 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]',
                        stickyBg,
                      )}
                    >
                      {rightCols.map((col) => renderBodyCell(col, item, index, isEligible, ctx))}
                    </div>
                  )}
                </div>
              </div>
            );
      }}
    />
  );
};
